/** @jsxRuntime automatic */
/** @jsxImportSource preact */
// OWNER: LG-ADMIN. The smoke report (§5.4) and the pilot report (§6.2) stored on LeadSearch.report, shown at the top of
// the Results tab for smoke and pilot searches (the Niches menu links here). Every smoke check is listed with pass or
// fail and the real observed value; a check the report doesn't carry is shown as missing, never as a pass. Unmeasured
// pilot numbers read "not measured". Hook-free and imports only ./logic, so the LG-ADMIN report test in tests/ renders it
// without a DOM (the pragmas above make Vitest's esbuild compile it for Preact).
import type { ComponentChildren } from 'preact';
import type { DiscoveryVendor, LeadSearch, PilotReport, SmokeCheck, SmokeReport } from '../../../src/shared/api-leadgen';
import {
  ESTIMATE_PRIORS, SMOKE_CHECKS, STATUS_LABELS, headToHeadVerdict, hitRateText, int, isPilotReport, isSmokeReport, pilotPrecisionText,
  rateText, reportTime, share, smokeCheckShort, smokeSummary, usd, type Tone,
} from './logic';

function Chip(props: { tone: Tone; children: ComponentChildren; title?: string }) {
  return <span class={`chip${props.tone ? ` chip-${props.tone}` : ''}`} title={props.title}>{props.children}</span>;
}

export type ReportSearch = Pick<LeadSearch, 'id' | 'mode' | 'status' | 'report'> & Partial<Pick<LeadSearch, 'discoveryVendor'>>;

/** Nothing for a Full or single search. For a smoke or pilot search: the report, or why there isn't one yet. */
export function SearchReportView(props: { search: ReportSearch; weekdayCloseThreshold?: string | null }) {
  const s = props.search;
  const r = s.report;
  if (isSmokeReport(r)) return <SmokeReportView report={r} />;
  if (isPilotReport(r)) return <PilotReportView report={r} weekdayCloseThreshold={props.weekdayCloseThreshold ?? null} discoveryVendor={s.discoveryVendor} />;
  if (s.mode !== 'smoke' && s.mode !== 'pilot') return null;
  const what = s.mode === 'smoke' ? 'Smoke report' : 'Pilot report';
  const ended = s.status === 'failed' || s.status === 'canceled';
  const finished = s.status === 'review' || s.status === 'imported';
  const notPassed = s.mode === 'smoke' ? ' The smoke has not passed.' : '';
  return (
    <section class="card stack lg-report" aria-label={what}>
      <div class="lg-card-head"><h3>{what}</h3><Chip tone={ended || finished ? 'bad' : ''}>{ended || finished ? 'no report' : 'not written yet'}</Chip></div>
      <div class="adm-muted-box">
        {ended
          ? `Search #${s.id} ended (${STATUS_LABELS[s.status]}) before its last stage, so no report was written.${notPassed} Run a new ${s.mode}.`
          : finished
            ? `Search #${s.id} finished (${STATUS_LABELS[s.status]}) but no report was stored.${notPassed} This is a server fault: check the search's errors in Searches.`
            : `The report is written when the last stage (S8) finishes. Search #${s.id} is ${STATUS_LABELS[s.status].toLowerCase()}.`}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- smoke (§5.4)

export function SmokeReportView(props: { report: SmokeReport }) {
  const r = props.report;
  const sum = smokeSummary(r);
  const known = new Set<string>(SMOKE_CHECKS.map((c) => c.key));
  // the report's own order (it carries all 11 in §5.4 order), then any §5.4 check it left out
  const rows: Array<{ key: string; label: string; check: SmokeCheck | null }> = [
    ...(r.checks ?? []).map((c) => ({ key: c.key, label: c.label, check: c })),
    ...sum.missing.map((k) => ({ key: k, label: smokeCheckShort(k), check: null })),
  ];
  return (
    <section class="card stack lg-report" aria-label="Smoke report">
      <div class="lg-card-head">
        <h3>Smoke report · v{r.nicheVersion}</h3>
        <Chip tone={sum.passed ? 'ok' : 'bad'}>{sum.passed ? 'passed' : 'failed'}</Chip>
        <span class="lg-note grow">{sum.text}.</span>
        <span class="lg-note">Estimate {usd(r.estimateUsd)} · actual {usd(r.actualUsd)} · written {reportTime(r.generatedAt)}</span>
      </div>
      {r.passed && !sum.passed ? <div class="lg-err-line">The server marked this smoke passed, but the checks below don't all pass. Treat it as failed.</div> : null}
      <div class="adm-table-wrap">
        <table class="lg-report-table">
          <thead><tr><th class="num">#</th><th>Check</th><th>Result</th><th>Observed value</th></tr></thead>
          <tbody>
            {rows.map((x, i) => {
              const pass = x.check?.pass === true;
              return (
                <tr class={pass ? '' : 'lg-row-bad'} data-check={x.key}>
                  <td class="num">{i + 1}</td>
                  <td>{x.label}<span class="lg-report-key">{known.has(x.key) ? x.key : `${x.key} (not a §5.4 check)`}</span></td>
                  <td>{x.check ? <Chip tone={pass ? 'ok' : 'bad'}>{pass ? 'pass' : 'fail'}</Chip> : <Chip tone="bad">missing</Chip>}</td>
                  <td class="lg-report-val">{x.check ? (x.check.value || 'no value reported') : 'Not in the report, so it counts as failed.'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- pilot (§6.2)

export function PilotReportView(props: { report: PilotReport; weekdayCloseThreshold: string | null; discoveryVendor?: DiscoveryVendor }) {
  const r = props.report;
  const prec = pilotPrecisionText(r.inNiche);
  // A pilot that discovered with DataForSEO (SPEC §19, 2026-10-02) has no Apify side to compare.
  const h2h = !r.headToHead && props.discoveryVendor === 'dataforseo'
    ? { text: 'Not compared: this pilot discovered with DataForSEO (no Apify run to compare).', tone: '' as const }
    : headToHeadVerdict(r.headToHead);
  const labels = [...r.labelsSeen].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  const closeMax = Math.max(1, ...r.weekdayCloseHistogram.map((b) => b.count));
  const closes = [...r.weekdayCloseHistogram].sort((a, b) => a.close.localeCompare(b.close));
  const m = r.measured;
  const measured: Array<{ label: string; value: string; prior: string }> = [
    { label: 'k (scrape events per place)', value: m.k === null ? 'not measured' : m.k.toFixed(2), prior: ESTIMATE_PRIORS.k.toFixed(2) },
    { label: 's (survival rate)', value: rateText(m.s), prior: ESTIMATE_PRIORS.s.toFixed(2) },
    { label: 'review regex hit rate', value: rateText(m.regexHitRate), prior: ESTIMATE_PRIORS.regexHitRate.toFixed(2) },
    { label: 'A/B tier rate', value: rateText(m.abRate), prior: ESTIMATE_PRIORS.abRate.toFixed(2) },
    { label: 'about-page rate', value: rateText(m.aboutPageRate), prior: ESTIMATE_PRIORS.aboutPageRate.toFixed(2) },
  ];
  const h = r.headToHead;
  return (
    <section class="card stack lg-report" aria-label="Pilot report">
      <div class="lg-card-head">
        <h3>Pilot report · v{r.nicheVersion}</h3>
        <span class="lg-note grow" />
        <span class="lg-note">Written {reportTime(r.generatedAt)}</span>
      </div>

      <div class="lg-est-nums">
        <div class="adm-stat" data-stat="in_niche"><b>{r.inNiche.precision === null ? '—' : r.inNiche.precision.toFixed(2)}</b><span>in-niche precision</span></div>
        <div class="adm-stat" data-stat="no_site"><b>{share(r.noSiteShare)}</b><span>no website</span></div>
        <div class="adm-stat" data-stat="mobile"><b>{share(r.mobileLineShare)}</b><span>mobile lines</span></div>
      </div>
      <div class="lg-note">
        In-niche: <Chip tone={prec.tone}>{prec.text}</Chip> · {int(r.inNiche.checked)} hand-checked: {int(r.inNiche.correct)} correct, {int(r.inNiche.wrong)} wrong, {int(r.inNiche.unsure)} unsure.
        {' '}Activation needs 0.85 on n ≥ 30 (check more in the Checks tab, detector in_niche).
      </div>

      <div class="lg-grid-2">
        <div class="stack" style="gap:6px">
          <span class="eyebrow">Measured rates (replace the §5.2 priors for this niche)</span>
          <table class="lg-report-table">
            <thead><tr><th>Rate</th><th class="num">Measured</th><th class="num">Prior</th></tr></thead>
            <tbody>{measured.map((x) => <tr data-measured={x.label}><td>{x.label}</td><td class="num">{x.value}</td><td class="num lg-note">{x.prior}</td></tr>)}</tbody>
          </table>
        </div>
        <div class="stack" style="gap:6px">
          <span class="eyebrow">Hit rate per detector</span>
          {r.detectorHitRates.length ? (
            <table class="lg-report-table">
              <thead><tr><th>Detector</th><th class="num">Hits</th></tr></thead>
              <tbody>{r.detectorHitRates.map((d) => <tr><td class="lg-mono">{d.detector}</td><td class="num">{hitRateText(d.hits, d.measured)}</td></tr>)}</tbody>
            </table>
          ) : <div class="lg-note">not measured</div>}
        </div>
      </div>

      <div class="lg-grid-2">
        <div class="stack" style="gap:6px">
          <span class="eyebrow">Labels seen ({labels.length}): trim the allowlist</span>
          {labels.length ? (
            <div class="adm-table-wrap lg-report-scroll">
              <table class="lg-report-table">
                <thead><tr><th>Label</th><th class="num">Places</th><th>List</th></tr></thead>
                <tbody>{labels.map((l) => (
                  <tr><td>{l.label}</td><td class="num">{int(l.count)}</td>
                    <td>{l.onDeny ? <Chip tone="bad">deny</Chip> : l.onAllow ? <Chip tone="ok">allow</Chip> : <Chip tone="warn">on neither</Chip>}</td></tr>
                ))}</tbody>
              </table>
            </div>
          ) : <div class="lg-note">not measured</div>}
        </div>
        <div class="stack" style="gap:6px">
          <span class="eyebrow">Chain groups found ({r.chainGroups.length}): confirm the blocklist</span>
          {r.chainGroups.length ? (
            <div class="adm-table-wrap lg-report-scroll">
              <table class="lg-report-table">
                <thead><tr><th>Group</th><th class="num">Places</th><th>Names</th></tr></thead>
                <tbody>{r.chainGroups.map((g) => <tr><td class="lg-mono">{g.key}</td><td class="num">{int(g.size)}</td><td class="lg-report-val">{g.names.join(' · ')}</td></tr>)}</tbody>
              </table>
            </div>
          ) : <div class="lg-note">No chain groups found.</div>}
        </div>
      </div>

      <div class="lg-grid-2">
        <div class="stack" style="gap:6px">
          <span class="eyebrow">Weekday close times{props.weekdayCloseThreshold ? ` (threshold now ${props.weekdayCloseThreshold})` : ''}</span>
          {closes.length ? (
            <div class="lg-hist">
              {closes.map((b) => (
                <div class={`lg-hist-row${props.weekdayCloseThreshold && b.close < props.weekdayCloseThreshold ? ' early' : ''}`} data-close={b.close}>
                  <span class="lg-mono">{b.close}</span>
                  <div><i style={`width:${((b.count / closeMax) * 100).toFixed(1)}%`} /></div>
                  <em>{int(b.count)}</em>
                </div>
              ))}
            </div>
          ) : <div class="lg-note">not measured</div>}
        </div>
        <div class="stack" style="gap:6px">
          <span class="eyebrow">Apify vs DataForSEO (§4.6){h ? ` · ${h.city}` : ''}</span>
          {h ? (
            <table class="lg-report-table">
              <thead><tr><th /><th class="num">Apify</th><th class="num">DataForSEO</th></tr></thead>
              <tbody>
                <tr><td>Places</td><td class="num">{int(h.apifyPlaces)}</td><td class="num">{int(h.dfsPlaces)}</td></tr>
                <tr><td>Phone fill</td><td class="num">{share(h.apifyPhoneFill)}</td><td class="num">{share(h.dfsPhoneFill)}</td></tr>
                <tr><td>Domain fill</td><td class="num">{share(h.apifyDomainFill)}</td><td class="num">{share(h.dfsDomainFill)}</td></tr>
                <tr><td>In both</td><td class="num" colSpan={2}>{int(h.overlap)}</td></tr>
                <tr><td>Claimed status agrees</td><td class="num" colSpan={2}>{share(h.claimedAgreement)}</td></tr>
                <tr><td>DataForSEO finds of Apify's in-niche</td><td class="num" colSpan={2}>{share(h.dfsCoverageOfApifyInNiche)}</td></tr>
              </tbody>
            </table>
          ) : null}
          <div><Chip tone={h2h.tone}>{h2h.text}</Chip></div>
        </div>
      </div>
    </section>
  );
}
